import { useCallback, useEffect, useState } from "react";
import { api, formatIDR, formatApiError, API } from "../lib/api";
import { toast } from "sonner";
import PaginationControls from "../components/PaginationControls";
import { usePagination } from "../hooks/usePagination";
import {
  Plus, Trash2, X, Search, Wallet, TrendingUp, TrendingDown, Download,
  Pencil, ArrowUpCircle, ArrowDownCircle, Settings, ChevronRight, ChevronLeft, Lock,
  BookOpen, Users, CheckCircle2, RotateCcw, RefreshCw, Target,
} from "lucide-react";
import { JournalTab } from "@/components/cashbook/JournalTab";
import { BookTab } from "@/components/cashbook/BookTab";
import { KasbonTab } from "@/components/cashbook/KasbonTab";
import { SummaryTab } from "@/components/cashbook/SummaryTab";

const inputCls = "rounded-none border border-zinc-300 bg-white px-3 py-2 text-sm w-full focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] focus:outline-none";

function todayISO() { return new Date().toISOString().slice(0, 10); }
function currentMonth() { return new Date().toISOString().slice(0, 7); }
function monthLabel(m) {
  const [y, mm] = m.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Ags", "Sep", "Okt", "Nov", "Des"];
  return `${names[parseInt(mm, 10) - 1]} ${y}`;
}
// Filter permanen Buku Kas: STRICT — hanya kasbon dgn status === 'PENDING' (backend normalize).
// Semua status lain (PAID/LUNAS/settled/paid/dll) DITOLAK — HARAM tampil di tab Buku Kas.
function isOpenKasbon(k) {
  if (!k) return false;
  // WHITELIST STRICT: hanya terima "PENDING" exact match.
  // Backend endpoint /cashbook/kasbon selalu normalize status ke "PENDING" atau "PAID".
  // Toleransi tambahan untuk backward-compat: "open"/"pending" (lowercase) dari data lama.
  const s = String(k.status || "").trim();
  if (s !== "PENDING" && s.toLowerCase() !== "open" && s.toLowerCase() !== "pending") return false;
  // Extra guard: kalau timestamp pelunasan ada → BUKAN pending
  if (k.settled_at || k.paid_at || k.date_settled) return false;
  // Nominal harus > 0
  if (Number(k.amount || 0) <= 0) return false;
  return true;
}

function prevMonthLabel(m) {
  // "2026-08" → "Jul 2026"; "2026-01" → "Des 2025"
  const [y, mm] = m.split("-").map((v) => parseInt(v, 10));
  const prevY = mm === 1 ? y - 1 : y;
  const prevM = mm === 1 ? 12 : mm - 1;
  const names = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Ags", "Sep", "Okt", "Nov", "Des"];
  return `${names[prevM - 1]} ${prevY}`;
}
function shiftMonth(m, delta) {
  // shiftMonth("2026-08", -1) → "2026-07"; shiftMonth("2026-12", 1) → "2027-01"
  const [y, mm] = m.split("-").map((v) => parseInt(v, 10));
  const total = y * 12 + (mm - 1) + delta;
  const nY = Math.floor(total / 12);
  const nM = (total % 12) + 1;
  return `${nY.toString().padStart(4, "0")}-${nM.toString().padStart(2, "0")}`;
}

export default function CashBook() {
  const [tab, setTab] = useState("journal");
  const [month, setMonth] = useState(currentMonth());
  const [accounts, setAccounts] = useState([]);
  const [txData, setTxData] = useState({ opening_balance: 0, transactions: [], closing_balance: 0 });
  const [summary, setSummary] = useState(null);
  const [balance, setBalance] = useState(null);
  const [setting, setSetting] = useState(null);
  // Kasbon open (semua waktu) — dipakai untuk mengurangi Saldo Kas Real-time & Saldo Akhir bulan.
  const [kasbonOpen, setKasbonOpen] = useState({ items: [], total_open: 0 });
  const [cashPlaza, setCashPlaza] = useState({ amount: 0, count: 0 });
  // Baris virtual Cash Plaza untuk di-inject ke Jurnal Akuntansi (filteredJournal).
  // Setiap baris = 1 payment cash/tunai di sale dengan branch=plaza + payment.date di bulan aktif.
  const [cashPlazaTxRows, setCashPlazaTxRows] = useState([]);
  // Buckets untuk Ringkasan Kategori — split per family & branch.
  // Diambil dari sale.payments[] (paid/dp), filter date di bulan aktif.
  const [payMix, setPayMix] = useState({
    cash_plaza:    { amount: 0, count: 0 },
    cash_kastem:   { amount: 0, count: 0 },
    bca_plaza:     { amount: 0, count: 0 },
    bca_kastem:    { amount: 0, count: 0 },
    mandiri_plaza: { amount: 0, count: 0 },
    mandiri_kastem:{ amount: 0, count: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openTx, setOpenTx] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [openSetting, setOpenSetting] = useState(false);
  const [openAccounts, setOpenAccounts] = useState(false);
  const [openAdjust, setOpenAdjust] = useState(false);
  const [openDiagnose, setOpenDiagnose] = useState(false);
  const [openMonthlyLock, setOpenMonthlyLock] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      // Compute first + last day of the month for Cash Plaza sales query.
      const [y, m] = month.split("-").map(Number);
      const first = `${y}-${String(m).padStart(2, "0")}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      const last = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;

      const [a, tx, s, b, st, kb] = await Promise.all([
        api.get("/cashbook/accounts"),
        api.get("/cashbook/transactions", { params: { month } }),
        api.get("/cashbook/summary", { params: { month } }),
        api.get("/cashbook/balance"),
        api.get("/cashbook/settings"),
        api.get("/cashbook/kasbon", { params: { status: "open" } }),
      ]);
      setAccounts(a.data);
      setTxData(tx.data);
      setSummary(s.data);
      setBalance(b.data);
      setSetting(st.data);
      // Cleanup: filter permanen — hanya kasbon dgn status open/pending yang dihitung.
      // Menangani data lama yang mungkin punya label status non-standard.
      const rawKasbon = kb.data.items || [];
      const openItems = rawKasbon.filter(isOpenKasbon);
      const openTotal = openItems.reduce((sum, k) => sum + Number(k.amount || 0), 0);
      setKasbonOpen({ items: openItems, total_open: openTotal });

      // Fetch sales 6 bulan ke belakang untuk menangkap pelunasan lintas bulan.
      const cpFetchFrom = new Date(y, m - 6, 1).toISOString().slice(0, 10);
      const cpFetchTo = last;
      const slBroad = await api.get("/sales", { params: { date_from: cpFetchFrom, date_to: cpFetchTo, limit: 10000 } });
      const salesRaw = Array.isArray(slBroad.data) ? slBroad.data : (slBroad.data.items || []);

      // === Klasifikasi setiap payment berdasarkan payment_method/payment_bank/branch ===
      // Family: cash | bca | mandiri (others ignored: shopee sudah lewat 301-SPP/SPK).
      // Branch: plaza | kastem (dari method suffix, atau fallback ke sale.branch, atau default plaza).
      const _classify = (p, saleBranch) => {
        const pm = (p.payment_method || "").toLowerCase();
        const pb = (p.payment_bank || "").toLowerCase();
        let family = null;
        if (pm.includes("cash") || pm === "tunai") family = "cash";
        else if (pm.includes("bca") || pb.includes("bca")) family = "bca";
        else if (pm.includes("mandiri") || pb.includes("mandiri")) family = "mandiri";
        if (!family) return null;
        let branch = null;
        if (pm.includes("kastem")) branch = "kastem";
        else if (pm.includes("plaza")) branch = "plaza";
        else branch = (saleBranch || "plaza").toLowerCase();
        if (branch !== "plaza" && branch !== "kastem") branch = "plaza";
        return { family, branch };
      };

      const buckets = {
        cash_plaza:    { amount: 0, count: 0 },
        cash_kastem:   { amount: 0, count: 0 },
        bca_plaza:     { amount: 0, count: 0 },
        bca_kastem:    { amount: 0, count: 0 },
        mandiri_plaza: { amount: 0, count: 0 },
        mandiri_kastem:{ amount: 0, count: 0 },
      };
      const CASH_LABEL = { cash_plaza: "Cash Plaza", cash_kastem: "Cash Kastem" };
      const CASH_CODE = { cash_plaza: "301-CP", cash_kastem: "301-CK" };
      const _cpVirtualTx = [];
      let cpAmount = 0, cpCount = 0;  // Cash Plaza only — dipertahankan untuk Ringkasan Kategori
      const _debug = [];

      for (const x of salesRaw) {
        const saleBranch = (x.branch || "plaza").toLowerCase();
        const payments = Array.isArray(x.payments) ? x.payments : [];
        let saleCashPlazaAmt = 0;

        if (payments.length > 0) {
          for (const p of payments) {
            const pDate = (p.date || p.created_at || x.date || "").slice(0, 10);
            if (pDate < first || pDate > last) continue;
            const cls = _classify(p, saleBranch);
            if (!cls) continue;
            const amt = Number(p.amount || 0);
            const bucketKey = `${cls.family}_${cls.branch}`;
            buckets[bucketKey].amount += amt;
            buckets[bucketKey].count += 1;

            // Emit virtual tx untuk Cash (Plaza + Kastem) ke Jurnal Akuntansi
            if (cls.family === "cash") {
              _cpVirtualTx.push({
                id: `cp:${x.sale_no}:${pDate}:${_cpVirtualTx.length}`,
                date: pDate,
                account_code: CASH_CODE[bucketKey],
                account_name: CASH_LABEL[bucketKey],
                type: "in",
                description: `${CASH_LABEL[bucketKey]} — ${x.customer_name || x.sale_no}`,
                reference: x.sale_no,
                amount: amt,
                _virtual: true,
              });
              if (cls.branch === "plaza") saleCashPlazaAmt += amt;
            }
          }
        } else {
          // Legacy fallback: no payments[]. Pakai payment_method di sale + sale.date.
          const cls = _classify({ payment_method: x.payment_method, payment_bank: x.payment_bank }, saleBranch);
          const sDate = (x.date || "").slice(0, 10);
          if (cls && sDate >= first && sDate <= last) {
            const amt = Number(x.total || 0) - Number(x.sisa_tagihan || 0);
            if (amt > 0) {
              const bucketKey = `${cls.family}_${cls.branch}`;
              buckets[bucketKey].amount += amt;
              buckets[bucketKey].count += 1;
              if (cls.family === "cash") {
                _cpVirtualTx.push({
                  id: `cp:${x.sale_no}:${sDate}:legacy`,
                  date: sDate,
                  account_code: CASH_CODE[bucketKey],
                  account_name: CASH_LABEL[bucketKey],
                  type: "in",
                  description: `${CASH_LABEL[bucketKey]} — ${x.customer_name || x.sale_no}`,
                  reference: x.sale_no,
                  amount: amt,
                  _virtual: true,
                });
                if (cls.branch === "plaza") saleCashPlazaAmt += amt;
              }
            }
          }
        }

        if (saleCashPlazaAmt > 0) {
          cpAmount += saleCashPlazaAmt;
          cpCount += 1;
          _debug.push({
            sale_no: x.sale_no, branch: x.branch, total: x.total, status: x.status,
            payments_count: payments.length, cash_plaza_paid: saleCashPlazaAmt,
          });
        }
      }
      setPayMix(buckets);
      setCashPlazaTxRows(_cpVirtualTx);
      // eslint-disable-next-line no-console
      console.log("[CashPlaza]", { month, cpAmount, cpCount, samples: _debug.slice(0, 20), totalSalesFetched: salesRaw.length });
      setCashPlaza({ amount: cpAmount, count: cpCount });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal memuat data");
    } finally { setLoading(false); }
  }, [month]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Search helper reused across tabs
  const matchesSearch = (term) => {
    if (!search) return true;
    const query = search.toLowerCase();
    return (term.description || "").toLowerCase().includes(query)
      || (term.account_name || "").toLowerCase().includes(query)
      || (term.account_code || "").toLowerCase().includes(query)
      || (term.reference || "").toLowerCase().includes(query);
  };

  // Tab Jurnal Akuntansi (JournalTab):
  // - PEMASUKAN: Semua type=in KECUALI 101
  // - PENGELUARAN: SEMUA type=out (dari akun mana pun — semua uang keluar mengurangi jurnal)
  const filteredJournal = txData.transactions
    .filter(t => t.account_code !== "101")
    .filter(matchesSearch)
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  
  // Tab Buku Kas (BookTab):
  // - KREDIT: HANYA akun 101 (kas fisik masuk), + description tanpa keyword "penjualan"
  // - DEBET: SEMUA type=out (dari akun mana pun — semua uang keluar mengurangi kas)
  // - Akun 102-PTP (Piutang Perusahaan) DILARANG muncul di Buku Kas (khusus Jurnal Akuntansi).
  const filteredBook = txData.transactions
    .filter(t => {
      if (t.account_code === "102-PTP") return false;
      if (t.type === "in") {
        return t.account_code === "101"
      }
      return t.type === "out";
    })
    .filter(matchesSearch);

  // Total Piutang Perusahaan (akun 102-PTP) — akumulatif dari semua transaksi bulan ini.
  const totalPiutangBulan = txData.transactions
    .filter(t => t.account_code === "102-PTP")
    .reduce((s, t) => s + (t.type === "in" ? Number(t.amount || 0) : -Number(t.amount || 0)), 0);

  // === Kartu Ringkasan DINAMIS mengikuti tab aktif ===
  // Buku Kas: totals dari filteredBook (akun 101 in + all out). Opening = cascade dari backend.
  // Jurnal Akuntansi: totals dari filteredJournal (akun non-101). Opening PAKSA = Rp 0 (permintaan user 2026-08-12).
  //   Rationale: Non-Kas / Jurnal Akuntansi tidak boleh cascade dari bulan sebelumnya —
  //   setiap bulan mulai bersih dari Rp 0.
  const _sumIn = (arr) => arr.reduce((s, t) => s + (t.type === "in" ? Number(t.amount || 0) : 0), 0);
  const _sumOut = (arr) => arr.reduce((s, t) => s + (t.type === "out" ? Number(t.amount || 0) : 0), 0);
  const bukuKasIn = _sumIn(filteredBook);
  const bukuKasOut = _sumOut(filteredBook);
  const bukuKasOpening = Number(txData.opening_balance || 0);
  const bukuKasClosing = bukuKasOpening + bukuKasIn - bukuKasOut;
  const jurnalIn = _sumIn(filteredJournal);
  const jurnalOut = _sumOut(filteredJournal);
  const jurnalOpening = 0; // Selalu Rp 0 di Jurnal Akuntansi — tidak cascade.
  const jurnalClosing = jurnalOpening + jurnalIn - jurnalOut;
  const cardData = tab === "journal"
    ? { in: jurnalIn, out: jurnalOut, closing: jurnalClosing, realtime: jurnalClosing, showKasbon: false }
    : { in: bukuKasIn, out: bukuKasOut, closing: bukuKasClosing, realtime: (balance?.balance ?? 0), showKasbon: true };

  const removeTx = async (t) => {
    if (t.auto) {
      // Cek dulu apakah orphan (sumber PO/Sale sudah dihapus)
      try {
        const chk = await api.get(`/cashbook/transactions/${t.id}/orphan-check`);
        if (!chk.data.is_orphan) {
          toast.error(`Transaksi otomatis dari ${chk.data.source_type} ${chk.data.reference}. Batalkan / hapus di modul sumbernya.`);
          return;
        }
        // Orphan — konfirmasi khusus
        if (!window.confirm(
          `Transaksi ini dibuat otomatis dari ${chk.data.source_type} ${chk.data.reference}, tapi sumbernya sudah dihapus (orphan).\n\nHapus transaksi kas ini sekarang?`
        )) return;
      } catch (err) {
        toast.error("Gagal cek status transaksi");
        return;
      }
    } else {
      if (!window.confirm("Apakah Anda yakin ingin menghapus data ini?")) return;
    }
    try { await api.delete(`/cashbook/transactions/${t.id}`); toast.success("Transaksi dihapus"); await loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Gagal"); }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/cashbook/export", { params: { month }, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Kas_Operasional_${month}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Gagal export"); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 sm:py-8 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-4 pb-6 border-b border-zinc-200">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Modul</div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight text-zinc-900 mt-1">Kas Operasional</h1>
          <p className="text-sm text-zinc-500 mt-1">Pencatatan kas harian dengan integrasi otomatis Penjualan & Pembelian.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="cash-setting-button" onClick={() => setOpenSetting(true)} className="rounded-none bg-white text-zinc-900 border border-zinc-300 px-4 py-2.5 text-sm hover:bg-zinc-50 inline-flex items-center gap-2" title="Saldo Awal">
            <Settings className="w-3.5 h-3.5" /> Saldo Awal
          </button>
          <button data-testid="cash-monthly-lock-button" onClick={() => setOpenMonthlyLock(true)} className="rounded-none bg-white text-[#F97316] border border-[#F97316]/40 px-4 py-2.5 text-sm hover:bg-[#F97316]/5 inline-flex items-center gap-2" title="Kunci Saldo Awal Bulan tertentu — override angka otomatis dengan angka pencatatan manual Anda">
            <Lock className="w-3.5 h-3.5" /> Kunci Saldo Bulan
          </button>
          <button data-testid="cash-diagnose-button" onClick={() => setOpenDiagnose(true)} className="rounded-none bg-white text-[#002FA7] border border-[#002FA7]/40 px-4 py-2.5 text-sm hover:bg-[#002FA7]/5 inline-flex items-center gap-2" title="Verifikasi rumus saldo — breakdown per akun untuk deteksi anomali">
            <Target className="w-3.5 h-3.5" /> Diagnose Saldo
          </button>
          <button data-testid="cash-accounts-button" onClick={() => setOpenAccounts(true)} className="rounded-none bg-white text-zinc-900 border border-zinc-300 px-4 py-2.5 text-sm hover:bg-zinc-50 inline-flex items-center gap-2">
            Kategori Akun
          </button>
          <button data-testid="cash-export-button" onClick={exportExcel} className="rounded-none bg-white text-zinc-900 border border-zinc-300 px-4 py-2.5 text-sm hover:bg-zinc-50 inline-flex items-center gap-2">
            <Download className="w-3.5 h-3.5" /> Export
          </button>
          <button data-testid="cash-tx-in-button" onClick={() => { setEditingTx({ type: "in" }); setOpenTx(true); }} className="rounded-none bg-[#008A00] text-white px-4 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-[#006D00] inline-flex items-center gap-2">
            <ArrowUpCircle className="w-4 h-4" /> Pemasukan
          </button>
          <button data-testid="cash-tx-out-button" onClick={() => { setEditingTx({ type: "out" }); setOpenTx(true); }} className="rounded-none bg-[#E81123] text-white px-4 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-[#C00E1F] inline-flex items-center gap-2">
            <ArrowDownCircle className="w-4 h-4" /> Pengeluaran
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 border-b border-zinc-200 flex items-center gap-1 flex-wrap">
        <TabBtn active={tab === "journal"} onClick={() => setTab("journal")} testId="tab-journal">Jurnal Akuntansi</TabBtn>
        <TabBtn active={tab === "book"} onClick={() => setTab("book")} testId="tab-book"><BookOpen className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Buku Kas</TabBtn>
        <TabBtn active={tab === "kasbon"} onClick={() => setTab("kasbon")} testId="tab-kasbon"><Users className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />Kasbon Sementara</TabBtn>
        <TabBtn active={tab === "summary"} onClick={() => setTab("summary")} testId="tab-summary">Ringkasan Kategori</TabBtn>
      </div>

      {/* Kartu Ringkasan — DINAMIS mengikuti tab aktif.
          Tab "Buku Kas" (journal): angka dari filteredBook (Akun 101 + semua OUT). Real-time = saldo kas fisik dari backend.
          Tab "Jurnal Akuntansi" (book): angka dari filteredJournal (Non-101). Real-time = closing bulan berjalan (tanpa balance API). */}
      {(tab === "journal" || tab === "book") && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
          <StatCard
            label={tab === "journal" ? "Saldo Non-Kas Real-time" : "Saldo Kas Real-time"}
            value={cardData.showKasbon ? (cardData.realtime - kasbonOpen.total_open) : cardData.realtime}
            icon={Wallet}
            big
            positive={((cardData.showKasbon ? (cardData.realtime - kasbonOpen.total_open) : cardData.realtime) >= 0)}
            testId="stat-balance"
            subValue={cardData.showKasbon && kasbonOpen.total_open > 0 ? `− Kasbon: ${formatIDR(kasbonOpen.total_open)}` : null}
          />
          <StatCard label={`Pemasukan ${monthLabel(month)}`} value={cardData.in} icon={TrendingUp} positive testId="stat-in-month" />
          <StatCard label={`Pengeluaran ${monthLabel(month)}`} value={cardData.out} icon={TrendingDown} danger testId="stat-out-month" />
          <StatCard
            label={`Saldo Akhir ${monthLabel(month)}`}
            value={cardData.showKasbon ? (cardData.closing - kasbonOpen.total_open) : cardData.closing}
            icon={Wallet}
            testId="stat-closing-month"
            positive={((cardData.showKasbon ? (cardData.closing - kasbonOpen.total_open) : cardData.closing) >= 0)}
            subValue={cardData.showKasbon && kasbonOpen.total_open > 0 ? `− Kasbon: ${formatIDR(kasbonOpen.total_open)}` : null}
          />
        </div>
      )}

      <div className="mt-5">
        {tab === "journal" && (
          <JournalTab
            month={month} setMonth={setMonth} monthLabel={monthLabel} prevMonthLabel={prevMonthLabel} shiftMonth={shiftMonth}
            search={search} setSearch={setSearch}
            txData={txData} filtered={filteredJournal} loading={loading}
            kasbonOpen={kasbonOpen}
            totalPiutang={totalPiutangBulan}
            onChanged={loadAll}
            onEdit={(t) => { setEditingTx(t); setOpenTx(true); }}
            onRemove={removeTx}
            onAdjustBalance={() => setOpenAdjust(true)}
            currentBalance={(balance?.balance ?? 0) - kasbonOpen.total_open}
            todayISO={todayISO}
          />
        )}
        {tab === "book" && (
          <BookTab
            month={month} setMonth={setMonth} monthLabel={monthLabel} prevMonthLabel={prevMonthLabel} shiftMonth={shiftMonth}
            search={search} setSearch={setSearch}
            txData={txData} filtered={filteredBook} loading={loading}
            kasbonOpen={kasbonOpen} isOpenKasbon={isOpenKasbon}
            onCashChanged={loadAll}
            onEdit={(t) => { setEditingTx(t); setOpenTx(true); }}
            onRemove={removeTx}
          />
        )}
        {tab === "kasbon" && (
          <KasbonTab month={month} setMonth={setMonth} onCashChanged={loadAll} prevMonthLabel={prevMonthLabel} shiftMonth={shiftMonth} inputCls={inputCls} todayISO={todayISO} />
        )}
        {tab === "summary" && (
          <SummaryTab summary={summary} month={month} setMonth={setMonth} cashPlaza={cashPlaza} payMix={payMix} prevMonthLabel={prevMonthLabel} shiftMonth={shiftMonth} />
        )}
      </div>

      {openTx && (
        <TxModal
          initial={editingTx}
          accounts={accounts}
          currentTab={tab}
          onClose={() => { setOpenTx(false); setEditingTx(null); }}
          onSaved={async () => { setOpenTx(false); setEditingTx(null); await loadAll(); }}
        />
      )}

      {openSetting && setting && (
        <SettingModal
          initial={setting}
          onClose={() => setOpenSetting(false)}
          onSaved={async () => { setOpenSetting(false); await loadAll(); }}
        />
      )}

      {openAccounts && (
        <AccountsModal
          accounts={accounts}
          onClose={() => setOpenAccounts(false)}
          onChanged={async () => { await loadAll(); }}
        />
      )}

      {openAdjust && (
        <AdjustBalanceModal
          currentBalance={(balance?.balance ?? 0) - kasbonOpen.total_open}
          onClose={() => setOpenAdjust(false)}
          onSaved={async () => { setOpenAdjust(false); await loadAll(); }}
        />
      )}

      {openDiagnose && (
        <DiagnoseSaldoModal
          month={month}
          onClose={() => setOpenDiagnose(false)}
        />
      )}

      {openMonthlyLock && (
        <MonthlyLockDialog
          currentMonth={month}
          onClose={() => setOpenMonthlyLock(false)}
          onSaved={async () => { await loadAll(); }}
        />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className={`px-5 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${active ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
      {children}
    </button>
  );
}

function StatCard({ label, value, icon: Icon, positive, danger, testId, big, subValue }) {
  const color = danger ? "text-[#E81123]" : positive ? "text-[#008A00]" : "text-zinc-900";
  return (
    <div className="bg-white p-4 lg:p-5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
        <Icon className="w-3.5 h-3.5 text-zinc-400" />
      </div>
      <div data-testid={testId} className={`font-mono ${big ? "text-2xl lg:text-3xl" : "text-xl lg:text-2xl"} tracking-tight font-bold mt-2 ${color}`}>
        {formatIDR(value)}
      </div>
      {subValue && (
        <div className="mt-1 text-[10px] font-mono text-[#F97316] font-semibold" title="Kasbon karyawan yang belum lunas, dikurangi dari saldo untuk cerminan kas fisik">{subValue}</div>
      )}
    </div>
  );
}

/* ---------- Transaction Modal ---------- */
function TxModal({ initial, accounts, currentTab, onClose, onSaved }) {
  const isEdit = initial && initial.id;
  const forcedType = initial?.type;
  // Akun 102-PTP (Piutang Perusahaan) HANYA boleh dipakai saat tab aktif = Jurnal Akuntansi (tab==="book").
  // Di tab Buku Kas (tab==="book") atau tab lain, sembunyikan dari picker.
  const eligible = accounts.filter((a) => {
    if (a.active === false) return false;
    if (a.type !== forcedType) return false;
    if (a.code === "102-PTP" && currentTab !== "book") return false;
    return true;
  });
  const [form, setForm] = useState({
    date: initial?.date || todayISO(),
    account_code: initial?.account_code || (eligible[0]?.code || ""),
    description: initial?.description || "",
    amount: initial?.amount || 0,
    reference: initial?.reference || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim()) { toast.error("Keterangan wajib diisi"); return; }
    if (Number(form.amount) <= 0) { toast.error("Jumlah harus > 0"); return; }
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        account_code: form.account_code,
        description: form.description.trim(),
        amount: Number(form.amount),
        reference: form.reference.trim() || null,
      };
      if (isEdit) {
        await api.put(`/cashbook/transactions/${initial.id}`, payload);
        toast.success("Transaksi diperbarui");
      } else {
        await api.post("/cashbook/transactions", payload);
        toast.success("Transaksi ditambahkan");
      }
      await onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const isIncome = forcedType === "in";
  const color = isIncome ? "#008A00" : "#E81123";

  return (
    <div className="fixed inset-0 z-50 bg-zinc-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-zinc-300 w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: color }}>
              {isIncome ? <ArrowUpCircle className="w-5 h-5 text-white" /> : <ArrowDownCircle className="w-5 h-5 text-white" />}
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{isEdit ? "Edit" : "Baru"}</div>
              <div className="font-heading text-xl font-bold text-zinc-900">{isIncome ? "Pemasukan Kas" : "Pengeluaran Kas"}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="close-tx-modal" className="p-1.5 hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tanggal">
              <input data-testid="tx-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls + " font-mono"} />
            </Field>
            <Field label="Kategori Akun">
              <select data-testid="tx-account" required value={form.account_code} onChange={(e) => setForm({ ...form, account_code: e.target.value })} className={inputCls}>
                {eligible.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Keterangan">
            <input data-testid="tx-desc" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={isIncome ? "Terima setoran, jual barang…" : "Bayar listrik, beli bensin…"} className={inputCls} />
          </Field>
          <Field label={isIncome ? "Jumlah Pemasukan (Rp)" : "Jumlah Pengeluaran (Rp)"}>
            <input data-testid="tx-amount" type="number" min="1" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls + " font-mono text-lg font-bold"} style={{ color }} />
          </Field>
          <Field label="Referensi (Opsional)" hint="Contoh: no. nota, no. PO, no. faktur">
            <input data-testid="tx-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className={inputCls + " font-mono text-sm"} />
          </Field>
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-zinc-200">
            <button type="button" onClick={onClose} className="rounded-none bg-white border border-zinc-300 px-5 py-2.5 text-sm hover:bg-zinc-50">Batal</button>
            <button data-testid="save-tx-button" type="submit" disabled={saving} className="rounded-none text-white px-8 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-40" style={{ backgroundColor: color }}>
              {saving ? "Menyimpan…" : "Simpan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------- Setting Modal (Saldo Awal) ---------- */
function SettingModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    opening_balance: initial.opening_balance || 0,
    opening_date: initial.opening_date || todayISO(),
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/cashbook/settings", { opening_balance: Number(form.opening_balance), opening_date: form.opening_date });
      toast.success("Saldo awal diperbarui");
      await onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal");
    } finally { setSaving(false); }
  };
  const resetToZero = async () => {
    if (!window.confirm("Reset Saldo Awal ke Rp 0? Aksi ini bisa dibatalkan dengan mengisi ulang saldo dan klik Simpan.")) return;
    setSaving(true);
    try {
      await api.put("/cashbook/settings", { opening_balance: 0, opening_date: form.opening_date });
      toast.success("Saldo awal berhasil di-reset ke Rp 0");
      await onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal reset");
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-zinc-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-zinc-300 w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
          <div className="font-heading text-xl font-bold">Saldo Awal Kas</div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <Field label="Tanggal Mulai" hint="Semua transaksi sebelum tanggal ini diabaikan">
            <input type="date" required data-testid="setting-date" value={form.opening_date} onChange={(e) => setForm({ ...form, opening_date: e.target.value })} className={inputCls + " font-mono"} />
          </Field>
          <Field label="Saldo Awal (Rp)">
            <input type="number" step="0.01" required data-testid="setting-balance" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} className={inputCls + " font-mono text-lg font-bold"} />
          </Field>
          <div className="flex flex-wrap justify-between items-center gap-2 pt-3 border-t border-zinc-200">
            <button
              type="button"
              data-testid="reset-setting-button"
              onClick={resetToZero}
              disabled={saving}
              className="rounded-none bg-white border border-[#E81123] text-[#E81123] px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#E81123]/5 disabled:opacity-40 inline-flex items-center gap-1.5"
              title="Reset Saldo Awal ke Rp 0"
            >
              <Trash2 className="w-3.5 h-3.5" /> Reset ke 0
            </button>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="rounded-none bg-white border border-zinc-300 px-5 py-2.5 text-sm hover:bg-zinc-50">Batal</button>
              <button data-testid="save-setting-button" type="submit" disabled={saving} className="rounded-none bg-[#002FA7] text-white px-6 py-2.5 text-sm font-bold uppercase tracking-wider disabled:opacity-40">{saving ? "Menyimpan…" : "Simpan"}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------- Accounts Modal (Kelola Kategori) ---------- */
function AccountsModal({ accounts, onClose, onChanged }) {
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", type: "out", active: true });
  const [saving, setSaving] = useState(false);

  const openAdd = () => { setEditing(null); setForm({ code: "", name: "", type: "out", active: true }); setOpenForm(true); };
  const openEdit = (a) => { setEditing(a); setForm({ code: a.code, name: a.name, type: a.type, active: a.active }); setOpenForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/cashbook/accounts/${editing.id}`, form);
      else await api.post("/cashbook/accounts", form);
      toast.success("Kategori tersimpan");
      setOpenForm(false);
      await onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal");
    } finally { setSaving(false); }
  };

  const remove = async (a) => {
    if (a.system) { toast.error("Akun sistem tidak bisa dihapus"); return; }
    if (!window.confirm(`Hapus kategori "${a.name}"?`)) return;
    try { await api.delete(`/cashbook/accounts/${a.id}`); toast.success("Kategori dihapus"); await onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Gagal"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-900/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-zinc-300 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-zinc-200 sticky top-0 bg-white">
          <div className="font-heading text-xl font-bold">Kategori Akun Kas</div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">
          <div className="flex justify-end mb-3">
            <button onClick={openAdd} data-testid="add-account-button" className="rounded-none bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#002FA7]/90 inline-flex items-center gap-2"><Plus className="w-3.5 h-3.5" /> Tambah</button>
          </div>
          <table className="w-full text-sm border border-zinc-200">
            <thead className="bg-zinc-50">
              <tr className="text-[10px] uppercase tracking-widest font-bold text-zinc-600">
                <th className="px-3 py-2 text-left">Kode</th>
                <th className="px-3 py-2 text-left">Nama Kategori</th>
                <th className="px-3 py-2 text-left">Tipe</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id} className="border-b border-zinc-100">
                  <td className="px-3 py-2 font-mono">{a.code}</td>
                  <td className="px-3 py-2">
                    {a.name}
                    {a.system && <span className="ml-2 text-[9px] uppercase tracking-widest bg-amber-100 text-amber-800 px-1.5 py-0.5">SYSTEM</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${a.type === "in" ? "text-[#008A00]" : "text-[#E81123]"}`}>
                      {a.type === "in" ? "Pemasukan" : "Pengeluaran"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-zinc-500">{a.active ? "Aktif" : "Nonaktif"}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openEdit(a)} className="p-1.5 hover:bg-zinc-100 text-zinc-700"><Pencil className="w-3.5 h-3.5" /></button>
                      {!a.system && <button onClick={() => remove(a)} className="p-1.5 hover:bg-[#E81123]/10 text-[#E81123]"><Trash2 className="w-3.5 h-3.5" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {openForm && (
          <div className="fixed inset-0 z-[60] bg-zinc-900/60 flex items-center justify-center p-4">
            <div className="bg-white border border-zinc-300 w-full max-w-md">
              <div className="flex items-center justify-between p-4 border-b border-zinc-200">
                <div className="font-heading font-bold">{editing ? "Edit Kategori" : "Kategori Baru"}</div>
                <button onClick={() => setOpenForm(false)} className="p-1.5 hover:bg-zinc-100"><X className="w-4 h-4" /></button>
              </div>
              <form onSubmit={submit} className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Kode Akun"><input required data-testid="acc-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className={inputCls + " font-mono"} placeholder="mis: 599" /></Field>
                  <Field label="Tipe">
                    <select required data-testid="acc-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
                      <option value="out">Pengeluaran</option>
                      <option value="in">Pemasukan</option>
                    </select>
                  </Field>
                </div>
                <Field label="Nama Kategori"><input required data-testid="acc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                  <span>Aktif</span>
                </label>
                <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
                  <button type="button" onClick={() => setOpenForm(false)} className="rounded-none bg-white border border-zinc-300 px-4 py-2 text-sm hover:bg-zinc-50">Batal</button>
                  <button data-testid="save-account-button" type="submit" disabled={saving} className="rounded-none bg-[#002FA7] text-white px-6 py-2 text-sm font-bold uppercase tracking-wider disabled:opacity-40">{saving ? "…" : "Simpan"}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-700 mb-1">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>}
    </label>
  );
}

function AdjustBalanceModal({ currentBalance, onClose, onSaved }) {
  const [target, setTarget] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const numeric = Number(String(target).replace(/[^\d.-]/g, "")) || 0;
  const delta = Math.round((numeric - Number(currentBalance || 0)) * 100) / 100;

  const submit = async (e) => {
    e.preventDefault();
    if (!target || numeric <= 0) {
      toast.error("Isi target saldo (Rupiah)");
      return;
    }
    if (Math.abs(delta) < 0.01) {
      toast.info("Saldo saat ini sudah sama dengan target");
      return;
    }
    if (!window.confirm(
      `Buat jurnal penyesuaian ${delta > 0 ? "MASUK" : "KELUAR"} sebesar Rp ${Math.abs(delta).toLocaleString("id-ID")}?\n\n` +
      `Saldo saat ini: Rp ${Number(currentBalance).toLocaleString("id-ID")}\n` +
      `Target baru:   Rp ${numeric.toLocaleString("id-ID")}\n\n` +
      `Jurnal ini akan tercatat permanen di Buku Kas dengan referensi ADJUSTMENT.`
    )) return;
    setSaving(true);
    try {
      const { data } = await api.post("/cashbook/adjust-balance", { target_balance: numeric, note });
      if (data.no_op) {
        toast.info(data.message || "Tidak ada penyesuaian dibuat");
      } else {
        toast.success(
          `Saldo diperbarui: ${formatIDR(data.current_balance)} → ${formatIDR(data.new_balance)} (delta ${delta > 0 ? "+" : ""}${formatIDR(delta)})`
        );
      }
      await onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Gagal update saldo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="adjust-balance-modal">
      <div className="bg-white w-full max-w-md p-6 border border-zinc-300">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#002FA7] font-bold flex items-center gap-1">
              <Target className="w-3 h-3" /> Jurnal Penyesuaian
            </div>
            <h2 className="font-heading text-xl font-bold text-zinc-900 mt-0.5">Update Saldo Kas Terakhir</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100" aria-label="Tutup"><X className="w-5 h-5" /></button>
        </div>

        <div className="bg-zinc-50 border border-zinc-200 p-3 mb-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Saldo Kas Real-time Sekarang</div>
          <div className="font-heading text-2xl font-bold text-zinc-900 font-mono mt-1">{formatIDR(currentBalance)}</div>
          <div className="text-[11px] text-zinc-500 mt-1">Formula: Saldo Awal + Σ(Kredit akun 101) − Σ(Debet semua akun) − Kasbon Belum Lunas</div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Target Saldo Baru (Rp)</label>
            <input
              type="number" step="1" min="0" required autoFocus
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="Contoh: 10921218"
              data-testid="adjust-balance-target"
              className={inputCls + " font-mono text-lg font-bold text-right"}
            />
            {target && (
              <div className={`mt-2 text-xs font-mono ${delta > 0 ? "text-[#008A00]" : delta < 0 ? "text-[#E81123]" : "text-zinc-500"}`}>
                Delta: {delta > 0 ? "+" : ""}{formatIDR(delta)} — Akan buat 1 jurnal {delta > 0 ? "MASUK (Kredit akun 101)" : delta < 0 ? "KELUAR (Debet akun 599-ADJ)" : "no-op"}
              </div>
            )}
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Catatan (opsional)</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Contoh: Rekonsiliasi kas fisik tgl 3 Agu"
              data-testid="adjust-balance-note"
              className={inputCls}
            />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200">
            <button type="button" onClick={onClose} className="rounded-none border border-zinc-300 bg-white px-4 py-2 text-sm hover:bg-zinc-50">Batal</button>
            <button type="submit" disabled={saving} data-testid="adjust-balance-submit" className="rounded-none bg-[#002FA7] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#002FA7]/90 disabled:opacity-50 inline-flex items-center gap-2">
              <Target className="w-4 h-4" />
              {saving ? "Memproses…" : "Buat Jurnal Penyesuaian"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ================================================================
   ===== Modal Diagnose Saldo — Verifikasi rumus & breakdown =====
   Menampilkan detail: Opening + Total Kredit − Total Debet = Saldo
   Plus breakdown per akun untuk spot anomali (mis. penjualan yg
   masuk revenue tapi belum tarik ke kas → tidak menambah saldo).
   ================================================================ */
function DiagnoseSaldoModal({ month, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("month");  // "month" | "all"

  useEffect(() => {
    (async () => {
      setLoading(true); setErr("");
      try {
        const params = mode === "month" ? { month } : {};
        const res = await api.get("/cashbook/diagnose", { params });
        setData(res.data);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || "Gagal memuat");
      } finally { setLoading(false); }
    })();
  }, [mode, month]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-zinc-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-zinc-900">Diagnose Saldo Kas</h2>
            <p className="text-xs text-zinc-500 mt-1">Verifikasi rumus: Opening + Total Kredit − Total Debet = Saldo</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Toggle mode: Bulan ini vs All-time */}
            <div className="inline-flex border border-zinc-300 text-[10px] font-bold uppercase tracking-widest">
              <button
                data-testid="diagnose-mode-month"
                onClick={() => setMode("month")}
                className={`px-3 py-1.5 ${mode === "month" ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
              >
                {monthLabel(month)}
              </button>
              <button
                data-testid="diagnose-mode-all"
                onClick={() => setMode("all")}
                className={`px-3 py-1.5 border-l border-zinc-300 ${mode === "all" ? "bg-[#002FA7] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
              >
                All-Time
              </button>
            </div>
            <button data-testid="diagnose-close" onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-2xl leading-none">×</button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {loading && <div className="text-zinc-400 font-mono text-sm">Memuat…</div>}
          {err && <div className="p-3 bg-[#E81123]/10 border border-[#E81123]/30 text-[#E81123] text-sm">{err}</div>}
          {data && (
            <>
              {/* Ringkasan Rumus SEDERHANA (yg user minta) — Opening + SEMUA Kredit − SEMUA Debet */}
              {data.simple && (
                <div className="p-4 bg-[#008A00]/5 border-2 border-[#008A00]/50">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[#008A00] mb-2">
                    Rumus Sederhana {mode === "month" ? `Bulan ${monthLabel(month)}` : "(all-time)"}
                  </div>
                  <div className="font-mono text-sm text-zinc-800 mb-3">
                    Saldo Sederhana = {mode === "month" ? "Saldo Awal" : "Opening"} + <b className="text-[#008A00]">Semua Kredit</b> − <b className="text-[#E81123]">Semua Debet</b>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <div className="text-zinc-500 uppercase tracking-widest text-[10px]">
                        {mode === "month" ? "Saldo Awal" : "Opening"}
                      </div>
                      <div className="font-mono font-bold text-zinc-900 mt-1">{formatIDR(data.opening_balance)}</div>
                    </div>
                    <div>
                      <div className="text-zinc-500 uppercase tracking-widest text-[10px]">+ Semua Kredit</div>
                      <div className="font-mono font-bold text-[#008A00] mt-1">{formatIDR(data.simple.total_in_all)}</div>
                    </div>
                    <div>
                      <div className="text-zinc-500 uppercase tracking-widest text-[10px]">− Semua Debet</div>
                      <div className="font-mono font-bold text-[#E81123] mt-1">{formatIDR(data.simple.total_out_all)}</div>
                    </div>
                    <div className="border-l-2 border-[#008A00] pl-3">
                      <div className="text-zinc-500 uppercase tracking-widest text-[10px]">= Hasil</div>
                      <div data-testid="diagnose-simple-balance" className="font-mono font-bold text-lg text-[#008A00] mt-1">{formatIDR(data.simple.balance)}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Ringkasan Rumus KAS (yg dipakai backend untuk Saldo Real-time) */}
              <div className="p-4 bg-[#002FA7]/5 border border-[#002FA7]/30">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] mb-2">
                  Rumus Kas (dipakai kartu Saldo Real-time) — {mode === "month" ? monthLabel(month) : "all-time"}
                </div>
                <div className="font-mono text-sm text-zinc-800 mb-3">
                  Saldo Kas = {mode === "month" ? "Saldo Awal" : "Opening"} + Kredit (HANYA akun 101) − Semua Debet
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div>
                    <div className="text-zinc-500 uppercase tracking-widest text-[10px]">
                      {mode === "month" ? "Saldo Awal" : "Opening"}
                    </div>
                    <div className="font-mono font-bold text-zinc-900 mt-1">{formatIDR(data.opening_balance)}</div>
                  </div>
                  <div>
                    <div className="text-zinc-500 uppercase tracking-widest text-[10px]">+ Kredit (101)</div>
                    <div className="font-mono font-bold text-[#008A00] mt-1">{formatIDR(data.total_in_kas_101)}</div>
                  </div>
                  <div>
                    <div className="text-zinc-500 uppercase tracking-widest text-[10px]">− Debet</div>
                    <div className="font-mono font-bold text-[#E81123] mt-1">{formatIDR(data.total_out_all_accounts)}</div>
                  </div>
                  <div className="border-l-2 border-[#002FA7] pl-3">
                    <div className="text-zinc-500 uppercase tracking-widest text-[10px]">= Saldo Kas</div>
                    <div data-testid="diagnose-balance" className="font-mono font-bold text-lg text-[#002FA7] mt-1">{formatIDR(data.balance_calculated)}</div>
                  </div>
                </div>
                <div className="mt-3 text-[10px] text-zinc-500 font-mono">
                  {data.tx_count_total} transaksi{mode === "month" ? " di bulan ini" : " total"} · {data.tx_count_kredit_101} kredit ke 101 · {data.tx_count_debet_all} debet
                  {data.adjustment_count > 0 && (
                    <> · <span className="text-[#E81123] font-bold">{data.adjustment_count} jurnal ADJUSTMENT</span></>
                  )}
                </div>
              </div>

              {/* Delta antara 2 rumus — bantu user mengerti selisih */}
              {data.simple && Math.abs(data.simple.balance - data.balance_calculated) > 0.01 && (
                <div className="p-3 bg-amber-50 border border-amber-300 text-xs">
                  <div className="font-bold text-amber-800 mb-1">
                    ⚠ Selisih 2 rumus: {formatIDR(data.simple.balance - data.balance_calculated)}
                  </div>
                  <div className="text-amber-900">
                    Perbedaan = {formatIDR(data.ignored_in_non_101.total_amount)} dari <b>{data.ignored_in_non_101.count} transaksi Penjualan/Pemasukan yg masuk akun revenue (301, 302, dll)</b>, bukan akun Kas (101).
                    Rumus Sederhana menghitung ini sebagai kas masuk, sedangkan Rumus Kas tidak (karena uang belum benar-benar masuk kas fisik — masih di platform Shopee/Bank).
                  </div>
                </div>
              )}

              {/* Ignored In (revenue non-101) — biasanya sumber kebingungan */}
              {data.ignored_in_non_101.count > 0 && (
                <div className="p-4 bg-amber-50 border border-amber-300">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-2">
                    ⚠ Transaksi type=in yang TIDAK menambah Saldo Kas ({data.ignored_in_non_101.count} tx · {formatIDR(data.ignored_in_non_101.total_amount)})
                  </div>
                  <div className="text-xs text-amber-900 mb-2">
                    Ini adalah penjualan/pemasukan yg masuk ke akun revenue (301, 302, dll) — <b>bukan kas fisik</b>.
                    Uang belum masuk kas sampai ditarik/disetor via akun 101. Cocok untuk Shopee/Bank Transfer yg saldonya masih di platform.
                  </div>
                  <div className="mt-2 max-h-40 overflow-y-auto border border-amber-200 bg-white">
                    <table className="w-full text-xs">
                      <thead className="bg-amber-100">
                        <tr>
                          <th className="px-2 py-1 text-left">Tanggal</th>
                          <th className="px-2 py-1 text-left">Akun</th>
                          <th className="px-2 py-1 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.ignored_in_non_101.sample.map((s, i) => (
                          <tr key={i} className="border-t border-amber-100">
                            <td className="px-2 py-1 font-mono">{s.date}</td>
                            <td className="px-2 py-1">{s.code} · {s.name}</td>
                            <td className="px-2 py-1 text-right font-mono">{formatIDR(s.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Kredit breakdown */}
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#008A00] mb-2">Breakdown Kredit (uang masuk kas 101)</div>
                {data.in_by_account.length === 0 ? (
                  <div className="text-zinc-400 text-xs font-mono">Tidak ada transaksi kredit ke akun 101.</div>
                ) : (
                  <div className="border border-zinc-200">
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest">
                        <tr>
                          <th className="px-3 py-2 text-left">Akun</th>
                          <th className="px-3 py-2 text-right">Jumlah Tx</th>
                          <th className="px-3 py-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.in_by_account.map((row, i) => (
                          <tr key={i} className="border-t border-zinc-100">
                            <td className="px-3 py-2 font-mono">{row.code} · {row.name}</td>
                            <td className="px-3 py-2 text-right font-mono">{row.count}</td>
                            <td className="px-3 py-2 text-right font-mono text-[#008A00] font-bold">{formatIDR(row.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Debet breakdown */}
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#E81123] mb-2">Breakdown Debet (uang keluar semua akun)</div>
                {data.out_by_account.length === 0 ? (
                  <div className="text-zinc-400 text-xs font-mono">Tidak ada transaksi debet.</div>
                ) : (
                  <div className="border border-zinc-200 max-h-60 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Akun</th>
                          <th className="px-3 py-2 text-right">Jumlah Tx</th>
                          <th className="px-3 py-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.out_by_account.map((row, i) => (
                          <tr key={i} className="border-t border-zinc-100">
                            <td className="px-3 py-2 font-mono">{row.code} · {row.name}</td>
                            <td className="px-3 py-2 text-right font-mono">{row.count}</td>
                            <td className="px-3 py-2 text-right font-mono text-[#E81123] font-bold">{formatIDR(row.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Cleanup actions — bantu cari duplikat & purge settings ganda */}
              <div className="p-3 border border-zinc-200 bg-zinc-50">
                <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-700 mb-2">🔧 Cleanup Data</div>
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="find-duplicate-tx-btn"
                    onClick={async () => {
                      try {
                        const params = mode === "month" ? { month } : {};
                        const r = await api.get("/cashbook/find-duplicate-tx", { params });
                        const dg = r.data.duplicate_groups;
                        if (dg === 0) {
                          toast.success("Tidak ada transaksi duplikat ditemukan.");
                        } else {
                          const sample = r.data.duplicates.slice(0, 3).map(d => `• ${d.date} ${d.description?.slice(0,30)} × ${d.count}`).join("\n");
                          toast.warning(`${dg} grup duplikat (${r.data.total_extra_txs} tx berlebih).\n\n${sample}`, { duration: 8000 });
                        }
                      } catch (e) { toast.error("Gagal cek duplikat"); }
                    }}
                    className="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 border border-amber-500 text-amber-700 bg-white hover:bg-amber-50"
                  >
                    Cari Duplikat Tx {mode === "month" ? monthLabel(month) : "All-Time"}
                  </button>
                  <button
                    data-testid="purge-cash-settings-btn"
                    onClick={async () => {
                      if (!window.confirm("Hapus dokumen cash_settings ganda? Yg dipertahankan = opening_balance tertinggi.")) return;
                      try {
                        const r = await api.post("/cashbook/purge-duplicate-cashbook-settings");
                        toast.success(`${r.data.deleted} duplicate settings dihapus. Opening tersisa: ${formatIDR(r.data.kept_opening_balance)}`);
                      } catch (e) { toast.error("Gagal cleanup"); }
                    }}
                    className="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 border border-[#E81123] text-[#E81123] bg-white hover:bg-[#E81123]/5"
                  >
                    Cleanup Setting Ganda
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div className="text-[11px] text-zinc-500 border-t border-zinc-200 pt-3">
                {data.notes.map((n, i) => <div key={i}>• {n}</div>)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MonthlyLockDialog({ currentMonth, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [month, setMonth] = useState(currentMonth);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/cashbook/monthly-openings");
      setItems(res.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal load kunci saldo");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const existing = items.find((x) => x.month === month);
    setAmount(existing ? String(existing.opening_balance) : "");
  }, [month, items]);

  const save = async () => {
    const num = Number(String(amount).replace(/[^\d.-]/g, ""));
    if (!month || Number.isNaN(num)) {
      toast.error("Bulan dan Nominal wajib diisi");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/cashbook/monthly-openings/${month}`, { opening_balance: num });
      toast.success(`Saldo Awal ${monthLabel(month)} dikunci ke ${formatIDR(num)}`);
      await load();
      await onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Hapus kunci Saldo Awal ${monthLabel(m)}? Sistem akan kembali menghitung otomatis dari data.`)) return;
    try {
      await api.delete(`/cashbook/monthly-openings/${m}`);
      toast.success("Kunci dihapus. Sistem hitung otomatis.");
      await load();
      await onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal hapus");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl border border-zinc-900 shadow-2xl" data-testid="monthly-lock-dialog">
        <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between bg-[#F97316]/5">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#F97316]">Override Saldo Awal</div>
            <h2 className="font-heading text-xl font-bold text-zinc-900 mt-0.5">Kunci Saldo Awal per Bulan</h2>
            <p className="text-xs text-zinc-500 mt-1">Angka ini akan dipakai sebagai Saldo Awal bulan itu. Pemasukan/Pengeluaran/Kasbon dihitung otomatis dari transaksi.</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900" data-testid="monthly-lock-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Bulan (YYYY-MM)">
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                data-testid="monthly-lock-month-input"
                className="w-full border border-zinc-300 rounded-none px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#F97316]"
              />
            </Field>
            <Field label="Nominal Saldo Awal (Rp)">
              <input
                type="text"
                inputMode="numeric"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="10462598"
                data-testid="monthly-lock-amount-input"
                className="w-full border border-zinc-300 rounded-none px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#F97316]"
              />
            </Field>
          </div>
          <button
            onClick={save}
            disabled={saving}
            data-testid="monthly-lock-save-button"
            className="w-full bg-[#F97316] text-white px-4 py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#EA6A0F] disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            <Lock className="w-4 h-4" /> {saving ? "Menyimpan…" : `Kunci Saldo Awal ${monthLabel(month)}`}
          </button>

          <div className="border-t border-zinc-200 pt-4">
            <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Daftar Bulan Terkunci</div>
            {loading ? (
              <div className="text-sm text-zinc-500">Loading…</div>
            ) : items.length === 0 ? (
              <div className="text-sm text-zinc-500 italic">Belum ada bulan yang dikunci. Sistem menghitung otomatis dari data.</div>
            ) : (
              <table className="w-full text-sm" data-testid="monthly-lock-list">
                <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
                  <tr>
                    <th className="text-left py-2">Bulan</th>
                    <th className="text-right py-2">Saldo Awal</th>
                    <th className="text-left py-2 pl-4">Update Terakhir</th>
                    <th className="text-right py-2">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.month} className="border-b border-zinc-100">
                      <td className="py-2.5 font-mono font-semibold">{monthLabel(it.month)} <span className="text-zinc-400">({it.month})</span></td>
                      <td className="py-2.5 text-right font-mono font-bold text-[#002FA7]">{formatIDR(it.opening_balance)}</td>
                      <td className="py-2.5 pl-4 text-[11px] text-zinc-500 font-mono">
                        {it.updated_at ? new Date(it.updated_at).toLocaleString("id-ID") : "-"}
                        {it.updated_by && <div className="text-zinc-400">oleh {it.updated_by}</div>}
                      </td>
                      <td className="py-2.5 text-right">
                        <button onClick={() => remove(it.month)} data-testid={`monthly-lock-delete-${it.month}`} className="text-[#E81123] hover:underline text-xs font-semibold">Hapus</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

